import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { isGeneratedDocument } from '@/lib/classifiable';
import type { ClassificationModelCatalog, Document } from '@/api/v2Client';
import { v2 } from '@/api/v2Client';
import { bandForPercent, confidencePercent, type ConfidenceSource } from '@/lib/confidence';

/**
 * Phase UI-13 — Classification tab panel.
 *
 * Tabular view of every document in the active section with the AI-
 * suggested classification, confidence %, current status, and per-row
 * Reclassify action. The "Classify all" button is exposed here for
 * convenience; the actual SSE wire-up lives in the parent so the panel
 * can stay pure-presentational.
 *
 * phase-classification-model-selection — this is where the classify
 * affordances live, so it is where the model selector lives: one small select
 * beside "Classify all", defaulting to the effective tenant setting, never a
 * modal step. The chosen id rides along with both the batch and the per-row
 * Reclassify. It is a REQUEST only — the server re-resolves precedence and
 * clamps to the tenant max tier before anything runs. The catalog comes from
 * the server (already cap-filtered), never a frontend constant, so a model
 * disabled in the admin catalog simply is not offered.
 */

export interface ClassificationTabPanelProps {
  readonly documents: readonly Document[];
  /** Receives the model the user selected ('' = the effective default). */
  readonly onClassifyAll?: (modelId?: string) => void;
  readonly onReclassify?: (documentId: string, modelId?: string) => void;
  readonly canClassify?: boolean;
  /**
   * Test seam: inject the catalog instead of fetching it. Production passes
   * nothing and the panel loads it from /api/v1/classification/model-catalog.
   */
  readonly modelCatalog?: ClassificationModelCatalog | null;
}

type StatusFilter = 'all' | 'unclassified' | 'classified' | 'confirmed' | 'rejected';

const wrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 13,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid var(--border)',
  padding: '4px 6px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontSize: 11,
};

const tdStyle: CSSProperties = {
  padding: '6px 6px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

const badgeBase: CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
};

// phase-compiler-screen-batch item 2 — this panel carried a THIRD set of band
// boundaries (85/65) while the Inspector used 70/40 and the CTD tree used 40.
// Three opinions about one number is what let the tree and the Inspector
// contradict each other on the same file. Boundaries now come from
// `lib/confidence`; only the colours stay local. Note this deliberately
// re-bands some rows (e.g. 74% was amber here, and is green under the shared
// 70 boundary) — one truth is the point of the change.
function confidenceBadge(doc: ConfidenceSource): {
  text: string;
  style: CSSProperties;
} {
  const pct = confidencePercent(doc);
  if (pct === null) {
    return {
      text: '—',
      style: { ...badgeBase, background: 'var(--surface2)', color: 'var(--text-muted)' },
    };
  }
  const band = bandForPercent(pct);
  if (band === 'high') {
    return {
      text: `${pct}%`,
      style: { ...badgeBase, background: 'rgba(34,197,94,0.18)', color: '#15803d' },
    };
  }
  if (band === 'medium') {
    return {
      text: `${pct}%`,
      style: { ...badgeBase, background: 'rgba(245,158,11,0.18)', color: '#92400e' },
    };
  }
  return {
    text: `${pct}%`,
    style: { ...badgeBase, background: 'rgba(239,68,68,0.18)', color: '#b91c1c' },
  };
}

function statusForDoc(doc: Document): StatusFilter {
  const status = (doc.ClassificationStatus as string | null | undefined) ?? null;
  const confirmedAt = (doc.ConfirmedAt as string | null | undefined) ?? null;
  const classification = (doc.Classification as string | null | undefined) ?? null;
  if (status === 'Rejected') return 'rejected';
  if (confirmedAt) return 'confirmed';
  if (classification) return 'classified';
  return 'unclassified';
}

/**
 * phase-classification-model-selection — the provenance chip shown beside the
 * confidence. A row classified before provenance was recorded renders an
 * honest em-dash: "not recorded" is the truth, and inventing a model name
 * would corrupt exactly the cheap-vs-expensive comparison this phase exists
 * to make possible.
 */
function modelProvenance(doc: Document): { text: string; title: string } {
  const model = (doc.ClassificationModel as string | null | undefined) ?? null;
  const tier = (doc.ClassificationModelTier as string | null | undefined) ?? null;
  if (!model) {
    return {
      text: '—',
      title:
        'Model not recorded. This document was classified before the model was stored on the row.',
    };
  }
  return {
    text: model,
    title: tier
      ? `Classified by ${model} (${tier} tier).`
      : `Classified by ${model}.`,
  };
}

export function ClassificationTabPanel({
  documents,
  onClassifyAll,
  onReclassify,
  canClassify = false,
  modelCatalog: injectedCatalog,
}: ClassificationTabPanelProps): ReactElement {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [catalog, setCatalog] = useState<ClassificationModelCatalog | null>(
    injectedCatalog ?? null,
  );
  // '' means "use the effective setting" — the least intrusive default: the
  // user opts INTO a different model, they never have to choose one.
  const [selectedModel, setSelectedModel] = useState<string>('');

  useEffect(() => {
    if (injectedCatalog !== undefined) {
      setCatalog(injectedCatalog);
      return;
    }
    let cancelled = false;
    void v2.documents
      .modelCatalog()
      .then((c) => {
        if (!cancelled) setCatalog(c);
      })
      // A catalog failure must never disable classifying — the selector simply
      // does not render and the server's effective default still applies.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [injectedCatalog]);

  const filtered = useMemo(
    () =>
      filter === 'all' ? documents : documents.filter((d) => statusForDoc(d) === filter),
    [documents, filter],
  );

  return (
    <div data-testid="classification-tab-panel" style={wrapperStyle}>
      <div style={headerStyle}>
        <select
          data-testid="classification-status-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as StatusFilter)}
          style={{
            padding: '4px 8px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <option value="all">All</option>
          <option value="unclassified">Unclassified</option>
          <option value="classified">Classified</option>
          <option value="confirmed">Confirmed</option>
          <option value="rejected">Rejected</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {catalog && catalog.models.length > 0 && (
          <select
            data-testid="classification-model-select"
            aria-label="Model for classification"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            title={`Default: ${catalog.effectiveDisplayName}. Your organisation caps models at the ${catalog.maxTier} tier.`}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 12,
              maxWidth: 220,
            }}
          >
            <option value="">
              {`Default model (${catalog.effectiveDisplayName})`}
            </option>
            {catalog.models.map((m) => (
              <option key={m.modelId} value={m.modelId}>
                {`${m.displayName} · ${m.tier}`}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          data-testid="classify-all-btn"
          disabled={!canClassify || !onClassifyAll}
          onClick={() => onClassifyAll?.(selectedModel || undefined)}
          style={{
            padding: '4px 12px',
            background: canClassify ? 'var(--accent, #4f46e5)' : 'var(--surface2)',
            color: canClassify ? '#fff' : 'var(--text-muted)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: 12,
            cursor: canClassify ? 'pointer' : 'not-allowed',
          }}
        >
          Classify all
        </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div
          data-testid="classification-empty"
          style={{
            padding: '16px 8px',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No documents match this filter.
        </div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Filename</th>
              <th style={thStyle}>AI Section</th>
              <th style={thStyle}>Confidence</th>
              {/* phase-classification-model-selection — provenance sits
                  immediately beside the confidence it qualifies: a 92% from a
                  fast model and a 92% from an advanced one are different
                  claims, and the comparison only works if both are visible. */}
              <th style={thStyle}>Model</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Classified By</th>
              <th style={thStyle}>Classified At</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc) => {
              const badge = confidenceBadge(doc);
              const status = statusForDoc(doc);
              return (
                <tr key={doc.Id} data-testid={`classification-row-${doc.Id}`}>
                  <td style={tdStyle}>{doc.FileName}</td>
                  <td style={tdStyle}>
                    {(doc.Classification as string | null | undefined) ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    <span
                      data-testid={`confidence-badge-${doc.Id}`}
                      style={badge.style}
                    >
                      {badge.text}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {(() => {
                      const prov = modelProvenance(doc);
                      return (
                        <span
                          data-testid={`classification-model-${doc.Id}`}
                          title={prov.title}
                          style={{
                            color: prov.text === '—' ? 'var(--text-muted)' : undefined,
                            fontFamily: prov.text === '—' ? undefined : 'var(--font-mono, monospace)',
                            fontSize: 11,
                          }}
                        >
                          {prov.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={tdStyle}>
                    {status}
                    {/* TODO-074 — when the autoConfirmHighConfidence gate
                        fired for this doc, surface an "auto" badge so the
                        publisher knows the confirmation was not from
                        manual review and may want to spot-check. */}
                    {doc.AutoConfirmed === true && (
                      <span
                        data-testid={`auto-confirmed-badge-${doc.Id}`}
                        title="Confirmed automatically because the AI confidence was at or above the autoConfirmHighConfidence threshold. Manual spot-check recommended."
                        style={{
                          marginLeft: 6,
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          background: 'rgba(99,102,241,0.14)',
                          color: '#3730a3',
                          border: '1px solid rgba(99,102,241,0.30)',
                        }}
                      >
                        ✓ auto
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {(doc.ConfirmedBy as string | null | undefined) ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    {(doc.ConfirmedAt as string | null | undefined) ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    {/* item 1 — generated documents carry no reclassify
                        affordance (template-assigned placement). */}
                    {!isGeneratedDocument(doc) && (
                    <button
                      type="button"
                      data-testid={`reclassify-btn-${doc.Id}`}
                      disabled={!canClassify || !onReclassify}
                      onClick={() => onReclassify?.(doc.Id, selectedModel || undefined)}
                      style={{
                        padding: '2px 8px',
                        fontSize: 11,
                        border: '1px solid var(--border)',
                        borderRadius: 3,
                        background: 'var(--surface)',
                        cursor: canClassify ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Reclassify
                    </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
